import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import WebsiteStepLinks from '@components/Publisher/Websites/Create/WebsiteStepLinks/WebsiteStepLinks';
import Input from '@uikit/Form/Input/Input';
import { Button } from '@uikit/Form/Button/Button';

import { createPublisherWebsiteApi } from '@api/publishers.api';
import useCurrentWorkspaceId from '@hooks/useCurrentWorkspaceId';
import { redirectUserTo } from '@utils/historyHandler';
import urls from '@constants/urls';
import { PublishersWebsiteDetailsType, PublishersWebsiteType } from '@ts/publishers.types';

import queryCacheKeys from '@constants/queryCacheKeys.constants';

import './WebsiteStepOne.css';

function WebsiteStepOne({
  website,
  updateWebsite,
}: {
  website: PublishersWebsiteDetailsType;
  updateWebsite: (website: PublishersWebsiteDetailsType) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const currentWorkspaceId = useCurrentWorkspaceId();
  const history = useHistory();
  const [websiteValue, setWebsiteValue] = useState(website?.domain || '');
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const handleWebsiteChange = useCallback((eOrValue) => {
    const next =
      eOrValue && typeof eOrValue === 'object' && 'target' in eOrValue
        ? eOrValue.target.value
        : eOrValue;
    setWebsiteValue(next ?? '');
  }, []);

  const updateWebsitesCache = (payload: PublishersWebsiteDetailsType) => {
    queryClient
      .getQueriesData({
        queryKey: [queryCacheKeys.publisherWebsites, currentWorkspaceId, payload.id],
      })
      .forEach((websites) => {
        const [cacheMeta] = websites;

        // find the first page
        if (cacheMeta[1] === 0) {
          queryClient.setQueryData(cacheMeta, (oldData: PublishersWebsiteType[]) => [
            payload,
            ...oldData,
          ]);
        }
      });
  };

  const handleContinue = useCallback(() => {
    if (isButtonLoading) {
      return;
    }
    setIsButtonLoading(true);
    if (website?.id > 0) {
      redirectUserTo(history, `${urls.WEBSITE_STEP_TWO(currentWorkspaceId, website.id)}`);
    } else {
      createPublisherWebsiteApi(currentWorkspaceId, websiteValue)
        .then((response) => {
          updateWebsite(response);
          updateWebsitesCache(response);
          redirectUserTo(history, `${urls.WEBSITE_STEP_TWO(currentWorkspaceId, response.id)}`);
        })
        .finally(() => setIsButtonLoading(false));
    }
  }, [websiteValue]);

  return (
    <div className="website-step" data-testid="website-wizard-step-1">
      <WebsiteStepLinks id={website?.id} isDisabledStep={!website?.id} step={1} />
      <div className="website-step-container step-1">
        <h2>Add a new website</h2>
        <p>Connect the websites to which you have access.</p>
        <p>You’ll start receiving orders once you complete the setup.</p>

        <div className="website-step-container__input">
          <Input
            isFullWidth
            value={websiteValue}
            onChange={handleWebsiteChange}
            placeholder="Enter your website URL"
            disabled={!!website?.domain}
            data-testid="website-wizard-domain"
          />
          <Button
            onClick={handleContinue}
            isLoading={isButtonLoading}
            data-testid="website-wizard-next"
            loadingText="Calculating ..."
            type={isButtonLoading ? 'blue' : 'primary'}
          >
            Continue
          </Button>
        </div>
      </div>
      <div className="website-step-container__dammy" />
    </div>
  );
}

export default WebsiteStepOne;
